/*
 * Copyright ish group pty ltd. All rights reserved. https://www.ish.com.au
 * No copying or use of this code is allowed without permission in writing from ish.
 */

import { Site } from '@api/model';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import React, { useEffect, useRef } from 'react';
import { NavLink, Route, Switch, useHistory } from 'react-router-dom';
import { getPortalWebsites, getSettings, getTags } from 'src/js/redux/actions/PortalWebsite';
import arrowRight from '../../../images/icon-arrow-right.svg';
import { useAppDispatch, useAppSelector } from '../../redux/hooks/redux';
import { makeAppStyles } from '../../styles/makeStyles';
import LeftMenu from '../common/LeftMenu';
import Billing from '../forms/Billing';
import Portal from '../forms/Portal';
import { SitesPage } from '../sites/SitesPage';

export const useStyles = makeAppStyles()((theme, prop, classes: any) => {
  return {
    navItem: {
      position: 'relative',
      color: theme.palette.text.secondary,
      left: 0,
      transition: 'all ease-out 0.2s',
      '&:before': {
        content: '\'\'',
        position: 'absolute',
        width: '16px',
        height: '12px',
        top: '4px',
        left: '-22px',
        opacity: 0,
        backgroundSize: 'contain',
        backgroundImage: `url(${arrowRight})`
      }
    },
    nav: {
      paddingLeft: 20,
      listStyle: 'none',
      '& > li': {
        marginTop: theme.spacing(2)
      },
      '& a': {
        textDecoration: 'none'
      },
    },
    navRoot: {
      textAlign: 'left',
      display: 'flex',
      justifyContent: 'center',
      marginTop: 70,
      flex: 1,
      overflow: 'auto',
      '& *': {
        fontFamily: 'Inter, sans-serif',
      },
    },
    activeNav: {
      [`& .${classes.navItem}`]: {
        left: '22px',
        color: theme.palette.primary.main,
      },
      [`& .${classes.navItem}:before`]: {
        opacity: 1,
      }
    },
    plusIcon: {
      position: 'absolute',
      top: -6
    },
    formItem: {
      backgroundColor: '#fff',
    },
    collapsable: {
      transition: theme.transitions.create('transform')
    },
    collapsed: {
      transform: 'rotate(180deg)'
    }
  };
});

interface SiteSettingsProps {
  s: Site;
  classes: Record<string, string>;
}

const SiteSettings = (
  {
    s,
    classes
  }: SiteSettingsProps) => {

  const mounted = useRef(false);

  useEffect(() => {
    mounted.current = true;
  }, [])
  
  return <li key={s.id}>
    <NavLink
      exact
      strict
      to={ `/websites/${s.id}/urls` }
      isActive={(m, location) => location.pathname.includes(`/websites/${s.id}/`)}
      className="centeredFlex"
      activeClassName={classes.activeNav}
    >
      <Typography variant="subtitle2" color="textSecondary" className={classes.navItem}>
        {s.key}
      </Typography>
    </NavLink>
  </li>
}

const Settings = () => {
  const { classes, cx } = useStyles();

  const sites = useAppSelector((state) => state.sites);
  const portals = useAppSelector(state => state.portalWebsite);

  const dispatch = useAppDispatch();

  const appHistory = useHistory();

  const onAddSite = () => {
    appHistory.push('/websites/new/urls');
  };

  const onAddPortal = () => {
    appHistory.push('/portals/new');
  };

  useEffect(() => {
    dispatch(getPortalWebsites());
    dispatch(getSettings());
    dispatch(getTags());
  }, []);

  return (
    <div className="root">
      <LeftMenu>
        <nav className={classes.navRoot}>
          <ul className={cx(classes.nav, 'pl-0')}>
            <li>
              <NavLink
                exact
                strict
                to="/"
                activeClassName={classes.activeNav}
              >
                <Typography variant="body2" className={cx(classes.navItem, 'heading')}>
                  Billing
                </Typography>
              </NavLink>
            </li>
            <li>
              <Typography className="heading relative" variant="body2" color="textSecondary">
                Portals
                <IconButton size="small" className={classes.plusIcon} onClick={onAddPortal}>
                  <AddCircleOutlineIcon color="primary" />
                </IconButton>
              </Typography>

              <ul className={classes.nav}>
                {portals.map((p) => (
                  <li key={p.id}>
                    <NavLink
                      exact
                      strict
                      to={`/portals/${p.id}`}
                      activeClassName={classes.activeNav}
                    >
                      <Typography variant="subtitle2" color="textSecondary" className={classes.navItem}>
                        {p.websiteName}
                      </Typography>
                    </NavLink>
                  </li>
                ))}
              </ul>
            </li>
            <li>
              <Typography className="heading relative" variant="body2" color="textSecondary">
                Websites
                <IconButton size="small" className={classes.plusIcon} onClick={onAddSite}>
                  <AddCircleOutlineIcon color="primary" />
                </IconButton>
              </Typography>
              <ul className={classes.nav}>
                {sites.map((s) => <SiteSettings key={s.id} s={s} classes={classes}  />)}
              </ul>
            </li>
          </ul>
        </nav>
      </LeftMenu>

      <div className="content">
        <div className="contentInner">
          <div className={classes.formItem}>
            <Switch>
              <Route exact path="/">
                <Billing />
              </Route>
              <Route exact path="/portals/:id">
                <Portal />
              </Route>
              <Route path="/websites/:id/:page">
                <SitesPage />
              </Route>
            </Switch>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;